import CampaignModel from 'cio/models/campaign';
import { WorkflowGraph, WorkflowGraphNode } from 'cio/utils/visual-workflow-builder/workflow-graph';
import {
  addPathLabelNodes,
  addReconnectNodes,
  removeSingularRejoinNodes,
  addDropNodes,
  addDummyNodes,
  createUnitNodesGraph,
  addGraphNodesAndEdges,
} from 'cio/utils/visual-workflow-builder/layout/preprocess';
import { layout } from 'cio/utils/visual-workflow-builder/layout/coordinates';
import {
  layout as brandesKopfLayout,
  recenterNodes,
} from 'cio/utils/visual-workflow-builder/layout/coordinates-brandes-kopf';
import { assignSizes } from 'cio/utils/visual-workflow-builder/layout/size';
import { rank } from 'cio/utils/visual-workflow-builder/layout/rank';
import {
  copyUnitNodeCoordinatesToOriginalNodes,
  normalizeCoordinates,
  calculateGraphSize,
} from 'cio/utils/visual-workflow-builder/layout/postprocess';
import { generatePaths } from 'cio/utils/visual-workflow-builder/layout/paths';
import { logTimeElapsed } from 'cio/utils/time';

// import config from 'cio/config/environment';

// const SHOULD_LOG = config.isProduction ? false : true;
const SHOULD_LOG = false;

function findFirstNodeId(workflowGraph: WorkflowGraph) {
  return Object.keys(workflowGraph.nodes).find((nodeId) => workflowGraph.inEdges[nodeId].length === 0);
}

export function processWorkflowGraph(campaign: CampaignModel, currentTab: string, useUnstableAlgorithm = false) {
  let workflowGraph = new WorkflowGraph(campaign);

  addGraphNodesAndEdges(campaign.consolidatedTree.actions, campaign.consolidatedTree.edges, workflowGraph);

  let firstNodeId = findFirstNodeId(workflowGraph);

  if (firstNodeId && campaign.actions.findBy('id', firstNodeId)?.type !== 'filter_match_delay_action') {
    let id = `v${workflowGraph.virtualNodeIndex}`;

    workflowGraph.addNode({
      id,
      type: 'virtual_enter',
      typeCategory: 'virtual',
      width: 0,
      height: 0,
      x: 0,
      y: 0,
    });
    workflowGraph.addEdge(id, firstNodeId, { type: 'continue' });

    workflowGraph.virtualNodeIndex++;
  }

  logTimeElapsed('parseTree', () => campaign.parseTree(), SHOULD_LOG);

  logTimeElapsed('addPathLabelNodes', () => addPathLabelNodes(workflowGraph), SHOULD_LOG);

  logTimeElapsed('addReconnectNodes', () => addReconnectNodes(workflowGraph), SHOULD_LOG);

  logTimeElapsed('removeSingularRejoinNodes', () => removeSingularRejoinNodes(workflowGraph), SHOULD_LOG);

  logTimeElapsed('addDropNodes', () => addDropNodes(workflowGraph), SHOULD_LOG);

  logTimeElapsed('assignSizes', () => assignSizes(workflowGraph, currentTab), SHOULD_LOG);

  let unitNodesGraph = logTimeElapsed(
    'createUnitNodesGraph',
    () => createUnitNodesGraph(workflowGraph, currentTab),
    SHOULD_LOG,
  );

  logTimeElapsed('rank', () => rank(unitNodesGraph), SHOULD_LOG);

  logTimeElapsed('addDummyNodes', () => addDummyNodes(unitNodesGraph), SHOULD_LOG);

  // After inserting dummy nodes we need to run the rank algorithm again
  logTimeElapsed('secondRank', () => rank(unitNodesGraph), SHOULD_LOG);

  if (useUnstableAlgorithm) {
    logTimeElapsed('layout', () => layout(unitNodesGraph), SHOULD_LOG);
  } else {
    // eslint-disable-next-line spellcheck/spell-checker
    logTimeElapsed('brandesKopfLayout', () => brandesKopfLayout(unitNodesGraph), SHOULD_LOG);
  }

  logTimeElapsed(
    'copyUnitNodeCoordinatesToOriginalNodes',
    () => copyUnitNodeCoordinatesToOriginalNodes(workflowGraph, unitNodesGraph),
    SHOULD_LOG,
  );

  if (!useUnstableAlgorithm) {
    logTimeElapsed('recenterRank', () => rank(workflowGraph), SHOULD_LOG);
    logTimeElapsed('recenterRejoinNodes', () => recenterNodes(workflowGraph), SHOULD_LOG);
  }

  logTimeElapsed('normalizeCoordinates', () => normalizeCoordinates(workflowGraph), SHOULD_LOG);
  logTimeElapsed('calculateGraphSize', () => calculateGraphSize(workflowGraph), SHOULD_LOG);
  logTimeElapsed('generatePaths', () => generatePaths(workflowGraph), SHOULD_LOG);

  return workflowGraph;
}

export function reprocessWorkflowGraphSizes(
  workflowGraph: WorkflowGraph,
  currentTab: string,
  useUnstableAlgorithm = false,
) {
  let workflowGraphCopy = new WorkflowGraph(workflowGraph.campaign);
  workflowGraphCopy.nodes = Object.keys(workflowGraph.nodes).reduce(
    (acc, key) => {
      acc[key] = { ...workflowGraph.nodes[key] };
      return acc;
    },
    {} as Record<string, WorkflowGraphNode>,
  );
  workflowGraphCopy.edges = workflowGraph.edges;
  workflowGraphCopy.paths = workflowGraph.paths;
  workflowGraphCopy.outEdges = workflowGraph.outEdges;
  workflowGraphCopy.inEdges = workflowGraph.inEdges;
  workflowGraphCopy.ranks = workflowGraph.ranks;
  workflowGraphCopy.traversalOrder = workflowGraph.traversalOrder;

  logTimeElapsed('assignSizes', () => assignSizes(workflowGraphCopy, currentTab), SHOULD_LOG);

  let unitNodesGraph = logTimeElapsed(
    'createUnitNodesGraph',
    () => createUnitNodesGraph(workflowGraphCopy, currentTab),
    SHOULD_LOG,
  );
  logTimeElapsed('rank', () => rank(unitNodesGraph), SHOULD_LOG);
  logTimeElapsed('addDummyNodes', () => addDummyNodes(unitNodesGraph), SHOULD_LOG);

  // After inserting dummy nodes we need to run the rank algorithm again
  logTimeElapsed('secondRank', () => rank(unitNodesGraph), SHOULD_LOG);

  if (useUnstableAlgorithm) {
    logTimeElapsed('layout', () => layout(unitNodesGraph), SHOULD_LOG);
  } else {
    // eslint-disable-next-line spellcheck/spell-checker
    logTimeElapsed('brandesKopfLayout', () => brandesKopfLayout(unitNodesGraph), SHOULD_LOG);
  }

  logTimeElapsed(
    'copyUnitNodeCoordinatesToOriginalNodes',
    () => copyUnitNodeCoordinatesToOriginalNodes(workflowGraphCopy, unitNodesGraph),
    SHOULD_LOG,
  );

  if (!useUnstableAlgorithm) {
    logTimeElapsed('recenterRank', () => rank(workflowGraphCopy), SHOULD_LOG);
    logTimeElapsed('recenterRejoinNodes', () => recenterNodes(workflowGraphCopy), SHOULD_LOG);
  }

  logTimeElapsed('normalizeCoordinates', () => normalizeCoordinates(workflowGraphCopy), SHOULD_LOG);
  logTimeElapsed('calculateGraphSize', () => calculateGraphSize(workflowGraphCopy), SHOULD_LOG);
  logTimeElapsed('generatePaths', () => generatePaths(workflowGraphCopy), SHOULD_LOG);

  return workflowGraphCopy;
}

// This is currently only used to obtain a traversalOrder on the WorkflowGraph
// that shows the same order the VWB would.
// If we traverse a campaign as-is, without inserting drop nodes etc, the order
// would be slightly different, due to items being on different ranks.
// In practice it seems like the order received from this function feels a bit
// more natural.
// If this turns out to be too expensive for large campaigns, we can always
// easily replace it with the simple traversal of edges as-is in future.
export function quickGraphRank(campaign: CampaignModel) {
  logTimeElapsed('parseTree', () => campaign.parseTree(), SHOULD_LOG);

  let workflowGraph = new WorkflowGraph(campaign);

  addGraphNodesAndEdges(campaign.consolidatedTree.actions, campaign.consolidatedTree.edges, workflowGraph);

  let firstNodeId = findFirstNodeId(workflowGraph);

  if (firstNodeId && campaign.actions.findBy('id', firstNodeId)?.type !== 'filter_match_delay_action') {
    let id = `v${workflowGraph.virtualNodeIndex}`;

    workflowGraph.addNode({
      id,
      type: 'virtual_enter',
      typeCategory: 'virtual',
      width: 0,
      height: 0,
      x: 0,
      y: 0,
    });
    workflowGraph.addEdge(id, firstNodeId, { type: 'continue' });

    workflowGraph.virtualNodeIndex++;
  }

  logTimeElapsed('addPathLabelNodes', () => addPathLabelNodes(workflowGraph), SHOULD_LOG);
  logTimeElapsed('addReconnectNodes', () => addReconnectNodes(workflowGraph), SHOULD_LOG);
  logTimeElapsed('removeSingularRejoinNodes', () => removeSingularRejoinNodes(workflowGraph), SHOULD_LOG);
  logTimeElapsed('addDropNodes', () => addDropNodes(workflowGraph), SHOULD_LOG);
  logTimeElapsed('rank', () => rank(workflowGraph), SHOULD_LOG);

  return workflowGraph;
}
